import PropTypes from 'prop-types'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import constants from 'app/constants'
import fetchJSONData from 'app/util/fetchJSONData'
import Content from 'components/content/Content/Content'
import ScrollRegion from 'components/core/ScrollRegion/ScrollRegion'
import Spinner from 'components/elements/Spinner/Spinner'
import classNames from 'lib/util/classNames'
import styles from './UtilityTermsDialog.module.scss'

let cache = null

/**
 * The `UtilityTermsDialog`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function UtilityTermsDialog (props) {
  const { className } = props
  const [data, setData] = useState(cache)
  const [error, setError] = useState(null)
  const isMounted = useRef(false)
  const classNameOutput = useMemo(
    () => classNames([className, styles.container]),
    [className]
  )
  const bodyRef = useRef()

  const loadData = useCallback(async () => {
    const { data, success } = await fetchJSONData(
      `${constants.PATH_DATA}/utility_terms.json?ver=${constants.CACHE_BUST_VERSION}`
    )

    if (!isMounted.current) {
      // No longer mounted
      return
    }

    if (!success) {
      setError('There was a problem loading the data. Please try again later.')

      return
    }

    const res = data

    cache = res

    setData(cache)
  }, [])

  const output = useMemo(() => {
    if (!data && !error) {
      return <Spinner />
    }

    if (error) {
      return (
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      )
    }

    return <Content items={data} />
  }, [data, error])

  /**
   * Effect: On mount
   */
  useEffect(() => {
    isMounted.current = true

    if (!cache) {
      loadData()
    }

    return () => {
      isMounted.current = false
    }
  }, [])

  return (
    <div className={classNameOutput}>
      <ScrollRegion className={styles.body} ref={bodyRef}>
        {output}
      </ScrollRegion>
    </div>
  )
}

/** @type {object} */
UtilityTermsDialog.propTypes = {
  className: PropTypes.string
}

/** @type {object} */
UtilityTermsDialog.defaultProps = {
  className: ''
}

// Memoize
export default memo(UtilityTermsDialog)
